import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  grpplayerlist,
  homepageadmin,
  playerpresent,
  sessionlist,
} from "../components/funtionsBD/Mainservice";
import {
  getHomeRolesFromUser,
  mapHomeRoleToUserType,
  readActiveHomeRole,
  readStoredUser,
} from "../utils/homeRole";
import { normalizeStoredGroupInfo } from "../utils/liveTrainingGroupInfo";
import {
  isGroupCoachedSession,
  isSessionFinalized,
  isSessionTodayOrTomorrow,
  parseSessionWindow,
  shouldEnterLiveTrainingAfterPresence,
} from "../utils/liveTrainingSessionWindow";
import {
  persistLiveTrainingSession,
  resolveGroupIdsForLiveTraining,
} from "./useLiveTrainingRedirect";
import { requestLiveFullscreen } from "./useLiveFullscreen";
import { resolveViewerPlayerId } from "../utils/trainingRoomAccess";
import {
  consumePresencePromptLoginFlag,
  deferPresencePrompt,
  peekPresencePromptLoginFlag,
  shouldOfferPresencePrompt,
  sortSessionsByStart,
} from "../utils/playerPresencePrompt";
import { addCalendarDays, formatCalendarDay } from "../utils/sessionCalendarAgenda";

const SKIP_PATHS = [
  "/",
  "/login",
  "/register",
  "/profiling",
  "/join",
  "/confirm-email",
  "/set-password",
  "/password-registration",
  "/complete_profile",
  "/club-admin-profiling",
  "/flows",
  "/pilot",
];

function shouldSkipPath(pathname) {
  return SKIP_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

function rememberGroupName(map, grId, grName) {
  const id = grId != null ? String(grId) : "";
  const name = typeof grName === "string" ? grName.trim() : "";
  if (!id || !name || map.has(id)) return;
  map.set(id, name);
}

/** listTS returns gr_id but not gr_name — resolve names from local/user/home data. */
async function resolveGroupNameMap(user, groupIds) {
  const map = new Map();
  for (const g of user?.groups || []) {
    rememberGroupName(
      map,
      g?.groupDetails?.gr_id || g?.gr_id,
      g?.groupDetails?.gr_name || g?.gr_name || g?.name
    );
  }
  try {
    const stored = JSON.parse(localStorage.getItem("grpinf") || "null");
    rememberGroupName(
      map,
      stored?.groupDetails?.gr_id || stored?.gr_id,
      stored?.groupDetails?.gr_name || stored?.gr_name
    );
  } catch {
    /* ignore */
  }
  const missing = (groupIds || []).some((id) => !map.has(String(id)));
  if (missing && user?.club_id) {
    try {
      const activeHomeRole = readActiveHomeRole(user);
      const mappedUserType = mapHomeRoleToUserType(activeHomeRole, user);
      const res = await homepageadmin({
        user_type: Number(mappedUserType),
        club_id: user.club_id,
        home_role: activeHomeRole,
      });
      for (const g of res?.data?.data?.groupList || []) {
        rememberGroupName(
          map,
          g?.groupDetails?.gr_id || g?.gr_id,
          g?.groupDetails?.gr_name || g?.gr_name
        );
      }
    } catch {
      /* keep partial map */
    }
  }
  return map;
}

function normalizeSession(raw, grId, groupNames) {
  if (!raw) return null;
  const resolvedGrId = raw.gr_id || grId;
  return {
    ...raw,
    ts_id: raw.ts_id || raw.id,
    gr_id: resolvedGrId,
    gr_name:
      raw.gr_name ||
      raw.group_name ||
      raw.tbl_group?.gr_name ||
      (resolvedGrId != null ? groupNames?.get(String(resolvedGrId)) : null) ||
      null,
  };
}

async function loadPendingSessions(user) {
  const groupIds = await resolveGroupIdsForLiveTraining(user);
  if (!groupIds.length) return [];
  const groupNames = await resolveGroupNameMap(user, groupIds);
  const now = Date.now();
  const justLoggedIn = peekPresencePromptLoginFlag();
  const collected = [];
  try {
    const res = await sessionlist({
      gr_ids: groupIds,
      date_from: formatCalendarDay(),
      date_to: addCalendarDays(new Date(), 1),
      include_hr: false,
    });
    const list = (res?.data?.group || [])
      .map((s) => normalizeSession(s, s.gr_id, groupNames))
      .filter((s) => s?.ts_id && isGroupCoachedSession(s) && !isSessionFinalized(s))
      .filter((s) => isSessionTodayOrTomorrow(s, now))
      .filter((s) => {
        const w = parseSessionWindow(s, now);
        return !w || w.endMs >= now;
      });
    collected.push(...list);
  } catch {
    /* no sessions */
  }
  const unique = [];
  const seen = new Set();
  for (const session of collected) {
    if (seen.has(session.ts_id)) continue;
    seen.add(session.ts_id);
    unique.push(session);
  }
  const sorted = sortSessionsByStart(unique, (s) => parseSessionWindow(s, now));
  const pending = [];
  for (const session of sorted) {
    let presence = null;
    let playerId = user.player_id != null ? String(user.player_id) : null;
    try {
      const rosterRes = await grpplayerlist({
        ts_id: session.ts_id,
        gr_id: session.gr_id,
      });
      const roster = rosterRes?.data?.data || [];
      playerId = resolveViewerPlayerId(user, roster) || playerId;
      const row = roster.find(
        (p) =>
          (playerId && String(p.player_id) === String(playerId)) ||
          (user.user_id && String(p.user_id) === String(user.user_id))
      );
      if (!row) continue;
      if (Object.prototype.hasOwnProperty.call(row, "presensee")) {
        presence = row.presensee;
      }
    } catch {
      /* treat as unanswered */
    }
    if (
      shouldOfferPresencePrompt({
        presence,
        tsId: session.ts_id,
        justLoggedIn,
      })
    ) {
      pending.push({
        ...session,
        playerId: playerId || user.user_id || null,
      });
    }
  }
  return pending;
}

export function usePlayerPresencePrompt() {
  const location = useLocation();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const current = queue[0] || null;
  const skip = shouldSkipPath(location.pathname || "");

  const refresh = useCallback(async () => {
    if (skip) {
      setQueue([]);
      return;
    }
    const user = readStoredUser();
    if (!getHomeRolesFromUser(user).includes("player")) {
      setQueue([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const pending = await loadPendingSessions(user);
      if (!pending.length) consumePresencePromptLoginFlag();
      setQueue(pending);
    } catch {
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, [skip]);

  useEffect(() => {
    if (skip) return undefined;
    if (!peekPresencePromptLoginFlag()) return undefined;
    refresh();
    return undefined;
  }, [refresh, skip]);

  const advance = useCallback(() => {
    setQueue((prev) => {
      const next = prev.slice(1);
      if (!next.length) consumePresencePromptLoginFlag();
      return next;
    });
    setError("");
  }, []);

  const answer = useCallback(
    async (present) => {
      const session = current;
      if (!session?.ts_id || submitting) return { ok: false };
      const user = readStoredUser();
      if (present && user && user.emailVerification !== true && user.is_demo_walkin !== true) {
        setError("emailVerificationRequiredBody");
        return { ok: false, error: "emailVerificationRequiredBody" };
      }
      setSubmitting(true);
      setError("");
      try {
        await playerpresent({
          ts_id: session.ts_id,
          gr_id: session.gr_id,
          ts_present: present,
          player_id: session.playerId || user?.player_id || user?.user_id,
        });
        window.dispatchEvent(new Event("smarttt-session-changed"));
        if (present && shouldEnterLiveTrainingAfterPresence(session)) {
          let stored = null;
          try {
            stored = JSON.parse(localStorage.getItem("grpinf") || "null");
          } catch {
            stored = null;
          }
          persistLiveTrainingSession(
            session,
            normalizeStoredGroupInfo(stored, session)
          );
          consumePresencePromptLoginFlag();
          setQueue([]);
          setError("");
          requestLiveFullscreen(undefined, { hideBrowserChrome: true });
          if (location.pathname !== "/live-training") {
            navigate("/live-training");
          }
          return { ok: true, redirected: true };
        }
        advance();
        return { ok: true };
      } catch (err) {
        const msg =
          err?.message ||
          err?.response?.data?.message ||
          "playerPresencePromptError";
        setError(msg);
        return { ok: false, error: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [advance, current, location.pathname, navigate, submitting]
  );

  const later = useCallback(() => {
    if (current?.ts_id) deferPresencePrompt(current.ts_id);
    advance();
  }, [advance, current]);

  return {
    open: Boolean(!skip && current?.ts_id),
    session: current,
    loading,
    submitting,
    error,
    confirm: () => answer(true),
    decline: () => answer(false),
    later,
  };
}
